import { useListingStepNavigation } from '@/lib/hooks/use-listing-step-navigation'
/**
 * New Listing — Step 2: Ownership
 *
 * An explicit, up-front question — "whose car is this?" — instead of silently
 * inferring it from the tarjeta. It drives the coverage path (mine / third
 * party → mandatory Qualitas; company → agency) and lets the documents step
 * reconcile the tarjeta's owner name against what the host declared, which is
 * what closes the "someone lists their boss's car" gap.
 */
import React, { useCallback, useState } from 'react'
import { View, Text } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Animated from 'react-native-reanimated'
import { Button } from '@/components/ui/Button'
import { TextInput } from '@/components/ui/TextInput'
import { Pressable } from '@/components/ui/Pressable'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { FormActionBar } from '@/components/ui/FormActionBar'
import { UserCheck, Users, Building2, Check } from '@/components/ui/icons'
import { type LucideIcon } from '@/components/ui/icons'
import { useListingWizardStore, type VehicleOwnershipType } from '@/lib/stores/listing-wizard.store'
import { useSemanticColors, SPACING, RADIUS } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { useTheme } from '@/lib/hooks/use-theme'
import { selection } from '@/lib/utils/haptics'
import { useEntrance } from '@/lib/hooks/use-entrance'

type OwnershipOption = {
  type: VehicleOwnershipType
  Icon: LucideIcon
  titleKey: string
  descKey: string
  testID: string
}

const OPTIONS: OwnershipOption[] = [
  {
    type: 'mine',
    Icon: UserCheck,
    titleKey: 'listing.ownership_mine_title',
    descKey: 'listing.ownership_mine_desc',
    testID: 'ownership.option-mine',
  },
  {
    type: 'third_party',
    Icon: Users,
    titleKey: 'listing.ownership_third_party_title',
    descKey: 'listing.ownership_third_party_desc',
    testID: 'ownership.option-third_party',
  },
  {
    type: 'company',
    Icon: Building2,
    titleKey: 'listing.ownership_company_title',
    descKey: 'listing.ownership_company_desc',
    testID: 'ownership.option-company',
  },
]

export default function NewListingOwnershipScreen() {
  const enter = useEntrance()
  const { t } = useTranslation()
  const { continueEditing, continueLabel } = useListingStepNavigation('features')
  const insets = useSafeAreaInsets()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  const ownership = useListingWizardStore((s) => s.ownership)
  const setOwnership = useListingWizardStore((s) => s.setOwnership)

  const [selected, setSelected] = useState<VehicleOwnershipType | null>(ownership?.type ?? null)
  const [authorizedByName, setAuthorizedByName] = useState(ownership?.authorizedByName ?? '')

  const handleSelect = useCallback((type: VehicleOwnershipType) => {
    void selection()
    setSelected(type)
  }, [])

  const needsAuthorizer = selected === 'third_party'
  const canContinue = selected !== null && (!needsAuthorizer || authorizedByName.trim().length > 1)

  const handleNext = useCallback(() => {
    if (!selected) return
    setOwnership({
      type: selected,
      authorizedByName: selected === 'third_party' ? authorizedByName.trim() : undefined,
    })
    continueEditing()
  }, [selected, authorizedByName, setOwnership, continueEditing])

  return (
    <ScreenContainer variant="content" scroll={false}>
      <KeyboardAwareView
        includeTopInset={false}
        contentContainerStyle={{
          padding: SPACING.md,
          paddingBottom: insets.bottom + SPACING['3xl'] + SPACING.xl,
        }}
      >
        <Animated.View collapsable={false} entering={enter(0)}>
          <Text
            style={{
              fontSize: 24,
              fontFamily: FONTS.bodyBold,
              color: colors.textPrimary,
              marginBottom: SPACING.xs,
            }}
          >
            {t('listing.ownership_title')}
          </Text>
          <Text
            style={{
              fontSize: 15,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
              lineHeight: 22,
              marginBottom: SPACING.lg,
            }}
          >
            {t('listing.ownership_subtitle')}
          </Text>
        </Animated.View>

        {OPTIONS.map((opt, index) => {
          const isSelected = selected === opt.type
          return (
            <Animated.View key={opt.type} collapsable={false} entering={enter(index + 1)}>
              <Pressable
                onPress={() => handleSelect(opt.type)}
                accessibilityRole="button"
                accessibilityState={{ selected: isSelected }}
                accessibilityLabel={t(opt.titleKey)}
                accessibilityHint={t(opt.descKey)}
                testID={opt.testID}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: SPACING.md,
                  padding: SPACING.md,
                  borderRadius: RADIUS.lg,
                  borderWidth: 2,
                  borderColor: isSelected ? COLORS.coral.DEFAULT : colors.border,
                  backgroundColor: colors.surface,
                  marginBottom: SPACING.sm,
                  ...(isSelected ? WEB_SHADOWS.sm : {}),
                }}
              >
                <View
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: RADIUS.md,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isSelected ? COLORS.coral.DEFAULT : colors.surfaceSubtle,
                  }}
                >
                  <opt.Icon
                    size={22}
                    color={isSelected ? '#FFFFFF' : colors.textSecondary}
                    strokeWidth={1.75}
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <Text
                    style={{
                      fontSize: 16,
                      fontFamily: FONTS.bodyBold,
                      color: colors.textPrimary,
                    }}
                  >
                    {t(opt.titleKey)}
                  </Text>
                  <Text
                    style={{
                      fontSize: 13,
                      fontFamily: FONTS.body,
                      color: colors.textSecondary,
                      lineHeight: 18,
                      marginTop: 2,
                    }}
                  >
                    {t(opt.descKey)}
                  </Text>
                </View>
                {isSelected ? (
                  <View
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 12,
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: COLORS.coral.DEFAULT,
                    }}
                  >
                    <Check size={15} color="#FFFFFF" strokeWidth={3} />
                  </View>
                ) : null}
              </Pressable>
            </Animated.View>
          )
        })}

        {needsAuthorizer ? (
          <Animated.View collapsable={false} entering={enter(1)} style={{ marginTop: SPACING.sm }}>
            <TextInput
              label={t('listing.ownership_authorizer_label')}
              value={authorizedByName}
              onChangeText={setAuthorizedByName}
              placeholder={t('listing.ownership_authorizer_placeholder')}
              autoCapitalize="words"
              testID="ownership.authorizer-name"
            />
            <Text
              style={{
                fontSize: 12,
                fontFamily: FONTS.body,
                color: colors.textMuted,
                lineHeight: 17,
                marginTop: SPACING.xs,
              }}
            >
              {t('listing.ownership_authorizer_hint')}
            </Text>
          </Animated.View>
        ) : null}
      </KeyboardAwareView>

      <FormActionBar style={{ backgroundColor: colors.background }}>
        <Button
          onPress={handleNext}
          variant="primary"
          size="lg"
          title={continueLabel}
          disabled={!canContinue}
          testID="ownership.continue-button"
        />
      </FormActionBar>
    </ScreenContainer>
  )
}
